<template>
  <div class="page-container">
    <!-- Left Side - Login Container -->
    <div class="left-side">
      <div class="login-box">
        <h3>{{ $t("auth.loginPage.title") }}</h3>
        <div class="auth-links">
          <p class="register-link">
            {{ $t("auth.loginPage.noAccount") }}
            <router-link to="/register">{{
              $t("auth.loginPage.register")
            }}</router-link>
          </p>
        </div>
        <form @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="email">{{ $t("auth.loginPage.emailLabel") }}</label>
            <input
              type="email"
              id="email"
              v-model="form.email"
              required
              class="form-control"
            />
          </div>
          <div class="form-group">
            <label for="password">{{
              $t("auth.loginPage.passwordLabel")
            }}</label>
            <div class="password-input-container">
              <input
                :type="showPassword ? 'text' : 'password'"
                id="password"
                v-model="form.password"
                required
                class="form-control"
              />
              <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <i
                  :class="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
                ></i>
              </button>
            </div>
            <div class="forgot-password-link">
              <router-link to="/forgot-password">{{
                $t("auth.loginPage.forgotPasswordLink")
              }}</router-link>
            </div>
          </div>
          <div v-if="error" class="error-message">
            {{ error }}
          </div>
          <button type="submit" :disabled="loading" class="btn-primary">
            <span class="button-content">
              {{
                loading
                  ? $t("auth.loginPage.loading")
                  : $t("auth.loginPage.loginButton")
              }}
              <i v-if="!loading" class="bi bi-arrow-right-short arrow-icon"></i>
            </span>
          </button>
        </form>
      </div>
      <div class="copyright">
        © 2025 AZIMUT GREEN SOLUTIONS SRL — ALL RIGHTS RESERVED — Via Fratelli Ugoni, 36 25126, Brescia — P.IVA 10200640968 — REA: BS - 608085 — CAP. SOC. EUR 50.000,00
      </div>
    </div>

    <!-- Right Side - Image Container -->
    <div class="right-side">
      <!-- The background image is now applied via CSS from the theme -->
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal-overlay" @click="showModal = false">
      <div class="modal-content" @click.stop>
        <h3>{{ $t("auth.loginPage.guestModalTitle") }}</h3>
        <p>
          {{ $t("auth.loginPage.guestModalMessage") }}
        </p>
        <button @click="showModal = false" class="btn-primary">
          {{ $t("auth.loginPage.closeButton") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useProjectSummaryStore } from "@/stores/project";
import { useI18n } from "vue-i18n"; // Assuming you're using vue-i18n

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const projectSummaryStore = useProjectSummaryStore();
const showModal = ref(false);

const form = reactive({
  email: "",
  password: "",
});

const loading = ref(false);
const error = ref("");
const detailedError = ref("");
const showPassword = ref(false);

const handleLogin = async () => {
  try {
    loading.value = true;
    error.value = "";
    detailedError.value = "";
    console.log("Attempting login with:", { email: form.email });

    // Login user
    await authStore.login({ email: form.email, password: form.password });

    router.push("/projects");
  } catch (err: any) {
    console.error("Login error:", err);
    // Show user-friendly error message
    if (err.response?.status === 401) {
      error.value = t("auth.loginPage.errors.invalidCredentials");
    } else if (err.response?.status === 400) {
      error.value = t("auth.loginPage.errors.badRequest");
    } else if (err.request) {
      error.value = t("auth.loginPage.errors.connectionError");
    } else {
      error.value = t("auth.loginPage.errors.unexpectedError");
    }
  } finally {
    loading.value = false;
  }
};

const enterAsGuest = () => {
  showModal.value = true;
};
</script>

<style scoped>
.page-container {
  height: 100vh;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.left-side {
  width: 60%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--color-white);
  height: 100vh;
}

.right-side {
  width: 40%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  overflow: hidden;
  background-image: var(--frame-login-bg);
  background-size: cover;
  background-position: center;
}

.login-image {
  /* This class is no longer used as the image is a background */
  display: none;
}

.login-box {
  background: var(--color-white);
  padding: 2rem;
  border-radius: 2px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.05),
    0 4px 12px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 400px;
}

h2 {
  text-align: center;
  margin-bottom: 0.8rem;
  color: var(--color-dark);
  font-weight: 600;
}

h3 {
  text-align: center;
  margin-bottom: 0.5rem;
  color: var(--color-dark);
}

.form-group {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--color-dark);
}

.form-control {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--color-gray);
  border-radius: 2px;
  font-size: 1rem;
}

.btn-primary {
  width: 100%;
  padding: 0.75rem;
  background-color: var(--primary-color);
  color: var(--color-white);
  border: none;
  border-radius: 2px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: filter 0.2s;
}

.btn-primary:hover {
  filter: brightness(0.9);
}

.btn-primary:disabled {
  background-color: var(--color-gray);
  cursor: not-allowed;
}

.error-message {
  color: var(--color-red);
  margin-bottom: 1rem;
  text-align: center;
}

.error-details {
  color: var(--color-dark);
  background-color: var(--color-yellow);
  border: 1px solid var(--color-yellow);
  padding: 0.75rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.forgot-password-link {
  text-align: right;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
}

.forgot-password-link a {
  color: var(--primary-color);
  text-decoration: underline;
}

.forgot-password-link a:hover {
  filter: brightness(0.9);
}

.auth-links {
  margin-bottom: 2.5rem;
}

.register-link {
  text-align: center;
  color: var(--color-dark);
  margin: 0;
}

.register-link a {
  color: var(--primary-color);
  text-decoration: underline;
}

.register-link a:hover {
  text-decoration: underline;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: var(--color-white);
  padding: 2rem;
  border-radius: 2px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 90%;
  text-align: center;
}

.modal-content h3 {
  margin-bottom: 1rem;
  color: var(--color-dark);
}

.modal-content p {
  margin-bottom: 1.5rem;
  color: var(--color-gray);
  line-height: 1.5;
}

.modal-content .btn-primary {
  width: auto;
  min-width: 120px;
}

.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.password-toggle {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--color-gray);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
}

.password-toggle:hover {
  color: var(--color-dark);
}

.password-toggle:focus {
  outline: none;
}

.password-toggle i {
  font-size: 0.8rem;
}

/* Ensure the password input doesn't overlap with the toggle button */
.password-input-container .form-control {
  padding-right: 40px;
}

/* Button content layout */
.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.arrow-icon {
  flex-shrink: 0;
  font-size: 20px;
}

.copyright {
  position: absolute;
  bottom: 2rem;
  width: 60%;
  color: var(--color-gray);
  font-size: 10px;
  text-align: center;
}
</style>
